import { hasComponent } from "bitecs";
import { Selected } from "../bit-components";
import configManager from "../singleton/ConfigManager";
import { SOUND_FREEZE, SOUND_THAW } from "../systems/sound-effects-system";

/**
 * Toggles visibility based on Selected component from ECS
 * @namespace ui
 * @component show-menu
 */
AFRAME.registerComponent("show-menu", {
  schema: {
    withPermission: { type: "string" },
    withoutPermission: { type: "string" },
    visibleIfOwned: { type: "boolean", default: true },
    layout: { default: "circle" },
    plane: { default: "xy" }
  },

  init() {
    try {
      this.updateVisibility = this.updateVisibility.bind(this);
      this.world = APP.world;
      this.targetEl = null;
      this.parentEl = this.el.parentNode;
      this.interactableMenu = document.getElementById("interactable-menu");
      this.scene = this.el.sceneEl;

      const interactableId =
        this.el.dataset.interactableId || this.el.closest("[data-interactable-id]")?.dataset.interactableId;
      this.targetEl = document.getElementById(interactableId);
      this.cam = document.getElementById("viewing-camera").object3DMap.camera;

      this.onChildrenInteract = () => {
        this.el.emit("interacted");
      };

      this.menuAnimationSystem = this.el.sceneEl.systems["hubs-systems"].menuAnimationSystem;
      if (this.menuAnimationSystem) {
        this.menuAnimationSystem.register(this, this.el, false);
      }
    } catch (error) {
      console.error("Error in show-menu init:", error);
    }
  },

  tick: (function () {
    const targetWorldPos = new THREE.Vector3();
    const cameraWorldPos = new THREE.Vector3();
    const direction = new THREE.Vector3();
    const menuWorldPos = new THREE.Vector3();

    return function () {
      try {
        if (!this.targetEl) return;
        this.updateVisibility(targetWorldPos, cameraWorldPos, direction, menuWorldPos);
      } catch (error) {
        console.error("Error in show-menu tick:", error);
      }
    };
  })(),

  play() {
    try {
      Array.from(this.el.children).forEach(child => {
        child.object3D.addEventListener("interact", this.onChildrenInteract);
        child.object3D.addEventListener("holdable-button-down", this.onChildrenInteract);
      });
    } catch (error) {
      console.error("Error in show-menu play:", error);
    }
  },

  pause() {
    try {
      Array.from(this.el.children).forEach(child => {
        child.object3D.removeEventListener("interact", this.onChildrenInteract);
        child.object3D.removeEventListener("holdable-button-down", this.onChildrenInteract);
      });
    } catch (error) {
      console.error("Error in show-menu pause:", error);
    }
  },

  remove() {
    try {
      this.pause();
      if (this.menuAnimationSystem) {
        this.menuAnimationSystem.unregister(this);
      }
    } catch (error) {
      console.error("Error in show-menu remove:", error);
    }
  },

  updateVisibility(targetWorldPos, cameraWorldPos, direction, menuWorldPos) {
    try {
      if (!configManager.config.VRUI.enabled) {
        if (this.el.object3D.visible) {
          this.el.object3D.visible = false;
          this.el.setAttribute("visible", false);
        }
        return;
      }

      const eid = this.targetEl.object3D.eid;
      const isVisible = this.el.object3D.visible;
      const isSelected = hasComponent(this.world, Selected, eid);

      const allowed = !!(
        (!this.data.withPermission && !this.data.withoutPermission) ||
        (this.data.withPermission &&
          window.APP.hubChannel &&
          window.APP.hubChannel.canOrWillIfCreator(this.data.withPermission)) ||
        (this.data.withoutPermission &&
          window.APP.hubChannel &&
          !window.APP.hubChannel.canOrWillIfCreator(this.data.withoutPermission))
      );

      const isTransforming = this.el.sceneEl.systems["transform-selected-object"].transforming;
      let shouldBeVisible = allowed && isSelected && !isTransforming;

      if (!this.data.visibleIfOwned) {
        shouldBeVisible = shouldBeVisible && !NAF.utils.isMine(this.targetEl);
      }

      if (isVisible !== shouldBeVisible) {
        this.el.setAttribute("visible", shouldBeVisible);

        if (shouldBeVisible) {
          if (!this.layoutInitialized) {
            this.el.setAttribute(
              "layout",
              `type: ${this.data.layout}; radius: 0.5; align: center; margin: 0.3; plane: ${this.data.plane};`
            );
            this.layoutInitialized = true;
          }

          const intersection = this.el.sceneEl.systems.interaction.getActiveIntersection();

          if (intersection && intersection.point) {
            targetWorldPos.copy(intersection.point);
            this.cam.getWorldPosition(cameraWorldPos);

            direction.subVectors(targetWorldPos, cameraWorldPos).normalize();
            menuWorldPos.copy(cameraWorldPos).add(direction.multiplyScalar(0.5));

            this.interactableMenu.setAttribute("position", menuWorldPos);
            this.interactableMenu.object3D.lookAt(cameraWorldPos);
          } else {
            this.targetEl.object3D.getWorldPosition(targetWorldPos);
            this.cam.getWorldPosition(cameraWorldPos);

            const directionV = direction.subVectors(targetWorldPos, cameraWorldPos).normalize();
            const distanceFromCamera = 0.5;
            const menuWorldPos = cameraWorldPos.clone().add(directionV.multiplyScalar(distanceFromCamera));

            this.interactableMenu.setAttribute("position", menuWorldPos);

            const menuObject3D = this.interactableMenu.object3D;
            menuObject3D.lookAt(cameraWorldPos);
          }

          if (this.hasRunBefore) {
            this.scene.systems["hubs-systems"].soundEffectsSystem.playSoundOneShot(SOUND_FREEZE);
          }
        } else {
          if (this.hasRunBefore) {
            this.scene.systems["hubs-systems"].soundEffectsSystem.playSoundOneShot(SOUND_THAW);
          }
        }
      }
      this.hasRunBefore = true;
    } catch (error) {
      console.error("Error in show-menu updateVisibility:", error);
    }
  }
});
